import type { z } from 'zod';
import { ApiErrorSchema, type ErrorCode } from '../contracts/errors';
import { createAnalyticsApi, type AnalyticsApi } from './analytics';
import { createBillingApi, type BillingApi } from './billing';
import { createCalculatorApi, type CalculatorApi } from './calculator';
import { createConfiguratorApi, type ConfiguratorApi } from './configurator';
import { createCooperationApi, type CooperationApi } from './cooperation';
import { createCuttingApi, type CuttingApi } from './cutting';
import { createDealersApi, type DealersApi } from './dealers';
import { createDecorsApi, type DecorsApi } from './decors';
import { createFeedbackApi, type FeedbackApi } from './feedback';
import { createFurnitureApi, type FurnitureApi } from './furniture';
import { createHandlesApi, type HandlesApi } from './handles';
import { createMediaApi, type MediaApi } from './media';
import { createMeApi, type MeApi } from './me';
import { createOnboardingApi, type OnboardingApi } from './onboarding';
import { createPaintApi, type PaintApi } from './paint';
import { createPartnersApi, type PartnersApi } from './partners';
import { createProfileApi, type ProfileApi } from './profile';
import { createReferralApi, type ReferralApi } from './referral';

/**
 * Типизированный API клиент для @pomoshnik. Используется фронтом (`apps/web`) и тестами.
 *
 * Принципы:
 * - Каждый response парсится через Zod-схему — гарантия типобезопасности на границе.
 * - 4xx/5xx → throw {@link ApiClientError} с распарсенным `code` из ApiError-конверта
 *   (или `PARSE_ERROR` если конверт не валидный).
 * - Сетевые ошибки (fetch reject) → throw `ApiClientError(NETWORK_ERROR)`.
 * - **Никаких авто-retry.** Retry на 5xx — обязанность Edge-слоя, не клиента
 *   (зафиксировано в плане Phase 3).
 * - `authToken` — функция, не значение: позволяет токену обновляться (Zustand store).
 * - `fetch` — DI для тестов (stub без сети).
 */

export type ApiClientErrorCode = ErrorCode | 'PARSE_ERROR' | 'NETWORK_ERROR';

export class ApiClientError extends Error {
  override readonly name = 'ApiClientError';

  constructor(
    public readonly status: number,
    public readonly code: ApiClientErrorCode,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  fetch?: typeof fetch;
  authToken?: () => string | null;
}

export type QueryValue = string | number | boolean | readonly string[] | undefined;

export interface RequestArgs<TSchema extends z.ZodTypeAny> {
  method: 'GET' | 'POST' | 'PUT';
  path: string;
  query?: Record<string, QueryValue>;
  body?: unknown;
  responseSchema: TSchema;
  /** При `true` добавляется `Authorization: Bearer <authToken()>`, если токен ненулевой. */
  auth?: boolean;
}

export type RequestFn = <TSchema extends z.ZodTypeAny>(
  args: RequestArgs<TSchema>,
) => Promise<z.infer<TSchema>>;

export interface ApiClient {
  request: RequestFn;
  handles: HandlesApi;
  furniture: FurnitureApi;
  decors: DecorsApi;
  dealers: DealersApi;
  media: MediaApi;
  paint: PaintApi;
  onboarding: OnboardingApi;
  me: MeApi;
  configurator: ConfiguratorApi;
  cooperation: CooperationApi;
  feedback: FeedbackApi;
  calculator: CalculatorApi;
  cutting: CuttingApi;
  partners: PartnersApi;
  analytics: AnalyticsApi;
  profile: ProfileApi;
  billing: BillingApi;
  referral: ReferralApi;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  // Late-binding: вызываем `globalThis.fetch` в момент request, а не захватываем
  // ссылку при создании клиента. Иначе MSW (или Service Worker), который патчит
  // globalThis.fetch ПОСЛЕ инициализации модуля, не сможет перехватить запросы.
  // Этот баг ловили в Phase 5 Block 4: singleton apiClient создавался при
  // загрузке модуля, а MSW server.listen() — позже в beforeAll → MSW не работал.
  const fetchImpl: typeof fetch = opts.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const authToken = opts.authToken;

  const request: RequestFn = async <TSchema extends z.ZodTypeAny>(
    args: RequestArgs<TSchema>,
  ): Promise<z.infer<TSchema>> => {
    const url = buildUrl(opts.baseUrl, args.path, args.query);
    const headers = new Headers();

    if (args.body !== undefined) {
      headers.set('content-type', 'application/json');
    }

    if (args.auth === true && authToken) {
      const token = authToken();
      if (token !== null) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    let response: Response;
    try {
      response = await fetchImpl(url, {
        method: args.method,
        headers,
        body: args.body !== undefined ? JSON.stringify(args.body) : undefined,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown network error';
      throw new ApiClientError(0, 'NETWORK_ERROR', message);
    }

    if (!response.ok) {
      // Try to parse ApiError envelope; if shape doesn't match, fall back to status-only error
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        body = null;
      }
      const parsed = ApiErrorSchema.safeParse(body);
      if (parsed.success) {
        throw new ApiClientError(
          response.status,
          parsed.data.error.code,
          parsed.data.error.message,
          parsed.data.error.details,
        );
      }
      throw new ApiClientError(
        response.status,
        'INTERNAL_ERROR',
        `Request failed with status ${response.status}`,
      );
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch (err) {
      throw new ApiClientError(
        response.status,
        'PARSE_ERROR',
        err instanceof Error ? err.message : 'Failed to parse JSON',
      );
    }

    const parsed = args.responseSchema.safeParse(body);
    if (!parsed.success) {
      throw new ApiClientError(
        response.status,
        'PARSE_ERROR',
        'Response shape does not match expected schema',
        { issues: parsed.error.issues },
      );
    }
    return parsed.data;
  };

  return {
    request,
    handles: createHandlesApi(request),
    furniture: createFurnitureApi(request),
    decors: createDecorsApi(request),
    dealers: createDealersApi(request),
    media: createMediaApi(request),
    paint: createPaintApi(request),
    onboarding: createOnboardingApi(request),
    me: createMeApi(request),
    configurator: createConfiguratorApi(request),
    cooperation: createCooperationApi(request),
    feedback: createFeedbackApi(request),
    calculator: createCalculatorApi(request),
    cutting: createCuttingApi(request),
    partners: createPartnersApi(request),
    analytics: createAnalyticsApi(request),
    profile: createProfileApi(request),
    billing: createBillingApi(request),
    referral: createReferralApi(request),
  };
}

function buildUrl(
  baseUrl: string,
  path: string,
  query: Record<string, QueryValue> | undefined,
): string {
  const url = new URL(path, baseUrl);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        if (value.length === 0) continue;
        for (const v of value) {
          url.searchParams.append(key, v);
        }
      } else {
        url.searchParams.append(key, String(value));
      }
    }
  }
  return url.toString();
}
